import { Link } from 'react-router-dom'

import type { NetworkPublicInfo } from '@tribeplatform/gql-client/types'
import { isImage } from '@tribeplatform/gql-client/utils'
import { EntityImage } from '@tribeplatform/react-components/common/components'
import { cn } from '@tribeplatform/react-components/utils/cn'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'
import { useColorScheme } from '@tribeplatform/react-ui-kit/ThemeProvider'

import {
  getWorkspaceDomain,
  buildWorkspaceLoginUrl,
} from './utils/workspaceUrl.js'

interface WorkspaceItemProps {
  workspace: NetworkPublicInfo
  isActive: boolean
}

export function AppSwitcherListItem({
  workspace,
  isActive,
}: WorkspaceItemProps) {
  const workspaceFullDomain = getWorkspaceDomain(workspace)
  const workspaceLoginUrl = buildWorkspaceLoginUrl(workspace)
  return (
    <Link
      to={workspaceLoginUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-3 w-full min-w-0 p-2 hover:bg-surface-hovered rounded-md',
        isActive && 'bg-surface-pressed',
      )}
    >
      <LogoRenderer workspace={workspace} />
      <div className="flex-1 min-w-0">
        <Text variant="bodyMd" fontWeight="medium" className="truncate block">
          {workspace.name}
        </Text>
        <Text variant="bodyXs" color="subdued" className="truncate block">
          {workspaceFullDomain}
        </Text>
      </div>
      {isActive && (
        <div className="shrink-0">
          <div className="w-5 h-5 rounded-full bg-content flex items-center justify-center">
            <SvgIcon name="check" className="w-3 h-3 text-surface" />
          </div>
        </div>
      )}
    </Link>
  )
}

function LogoRenderer({ workspace }: { workspace: NetworkPublicInfo }) {
  const { colorSchemeMode } = useColorScheme()

  const darkLogo = isImage(workspace.images?.darkMobileLogo)
    ? workspace.images?.darkMobileLogo
    : null
  const lightLogo = isImage(workspace.images?.lightMobileLogo)
    ? workspace.images?.lightMobileLogo
    : null
  const logo = colorSchemeMode === 'dark' ? darkLogo : lightLogo

  if (!logo) {
    return <WorkspaceLogoPlaceholder name={workspace.name} />
  }

  return <EntityImage size="2x" entity={{ image: logo }} />
}

function WorkspaceLogoPlaceholder({ name }: { name: string }) {
  const initials = name
    ?.split(/\s+/)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase())
    .join('')

  return (
    <div className="h-[2rem] w-[2rem] shrink-0 rounded-base bg-surface-pressed flex items-center justify-center">
      <span className="text-xs font-medium text-content-subdued">
        {initials || '?'}
      </span>
    </div>
  )
}
